import React, { useEffect, useMemo, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import { Badge, Select, Spinner, Table, TextInput } from 'flowbite-react';
import { useQuery } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  HiOutlineClipboardCopy,
  HiOutlineExclamation,
  HiOutlineSearch,
} from 'react-icons/hi';

import Title from '../../components/Title';
import Button from '../../components/Button';
import Notification from '../../components/Notification';
import PaginationFlowBite from '../../components/PaginationFlowBite';
import { PRINT_SHOP_PER_PAGE_OPTIONS } from '../../components/paginationItems';
import {
  DEFAULT_PAGE_SIZE,
  fetchTicketCounts,
  fetchTickets,
} from './api';
import {
  copyToClipboard,
  formatDate,
  isSlaBreach,
  SLA_THRESHOLD_DAYS,
  shortOrderId,
  statusBadgeColor,
  statusText,
  ticketPath,
} from './helpers';
import { PastOrderLookup } from './PastOrderLookup';
import { TICKET_QUEUE_DEFAULTS } from './queues';
import { formatMessage, printShopStrings } from './strings';
import {
  AssetKind,
  TicketOrigin,
  TicketStatus,
  TicketStatusFilter,
} from './types';

const SEARCH_DEBOUNCE_MS = 300;

export interface PrintShopQueueProps {
  /**
   * Which queue this page is. Books and mail-on-demand are different jobs —
   * different machines, different shipping shape — so each has its own page
   * and menu entry (abo-print-shop-menu-split ADR-001), each with its own
   * paid/accepted/in-production/shipped lanes and its own counters. Defaults
   * to `mod`, the console's original queue.
   */
  origin?: TicketOrigin;
}

/**
 * Print-shop queue for one work type, oldest paid first (the backend owns the
 * sort). Status tabs with live counts, member/order search, pagination, and an
 * SLA highlight on stale paid tickets. Rows link to detail.
 *
 * Both queue routes render this component, so each route element carries a
 * `key`: without it React would keep one page's tab, search and page when the
 * sidebar moves to the other.
 */
export default function PrintShopQueue({ origin = 'mod' }: PrintShopQueueProps) {
  const navigate = useNavigate();
  const { defaultTab, tabs, allExcludesImported } =
    TICKET_QUEUE_DEFAULTS[origin];
  const [activeTab, setActiveTab] = useState<TicketStatusFilter>(defaultTab);
  const [assetKindFilter, setAssetKindFilter] = useState<AssetKind | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Debounce the search box into the query param so each keystroke does not
  // hit the backend (the api layer translates UI page → DS 0-based).
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const {
    data,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery(
    [
      'print-shop-tickets',
      origin,
      activeTab,
      assetKindFilter,
      search,
      page,
      pageSize,
    ],
    () =>
      fetchTickets({
        status: activeTab,
        assetKind: assetKindFilter || undefined,
        origin,
        excludeImported: allExcludesImported,
        search,
        page,
        limit: pageSize,
      }),
    { keepPreviousData: true, refetchOnWindowFocus: false },
  );

  // Scoped to this page's origin so its badges count ITS OWN backlog — an
  // unscoped count would show the mailing queue's numbers while looking at
  // Books.
  const { data: counts, refetch: refetchCounts } = useQuery(
    ['print-shop-ticket-counts', origin],
    () => fetchTicketCounts(origin),
    { refetchOnWindowFocus: false },
  );

  const tickets = data?.data ?? [];
  const meta = data?.meta;

  // The All badge must describe the All rows. Where All leaves the imported
  // archive out (Books, ADR-002), so does its count: `total` from DS still
  // includes it, and the per-status breakdown is already on the wire.
  const tabCount = useMemo(
    () => (status: TicketStatusFilter) => {
      if (status !== 'all') {
        return counts?.[status];
      }
      if (counts?.total === undefined) {
        return undefined;
      }
      return allExcludesImported
        ? counts.total - (counts.imported ?? 0)
        : counts.total;
    },
    [counts, allExcludesImported],
  );

  const selectTab = (tab: TicketStatusFilter) => {
    setActiveTab(tab);
    setPage(1);
  };

  const onRefresh = () => {
    refetch();
    refetchCounts();
  };

  const onCopyOrderId = async (
    event: React.MouseEvent,
    orderId: string,
    value: string,
  ): Promise<void> => {
    event.stopPropagation();
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopiedId(orderId);
      setTimeout(() => setCopiedId(null), 1500);
    }
  };

  // Cmd/Ctrl+click and middle-click on a row open the ticket in a new tab, as
  // they do on the order-number link; a plain click opens it here.
  const onRowClick = (event: React.MouseEvent, orderId: string): void => {
    const path = ticketPath(orderId);
    if (event.metaKey || event.ctrlKey || event.button === 1) {
      window.open(path, '_blank', 'noopener');
      return;
    }
    navigate(path);
  };

  const renderTab = (tab: TicketStatusFilter) => {
    const count = tabCount(tab);
    const label =
      tab === 'all'
        ? printShopStrings.queue.tabAll
        : printShopStrings.statusLabels[tab];
    const isActive = activeTab === tab;
    return (
      <button
        key={tab}
        type="button"
        data-testid={`ticket-tab-${tab}`}
        onClick={() => selectTab(tab)}
        className={`flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-semibold ${
          isActive
            ? 'border-primary-500 text-primary-600'
            : 'border-transparent text-neutral-500 hover:text-neutral-800'
        }`}
      >
        {label}
        {count !== undefined && (
          <span
            className={`rounded-full px-2 py-0.5 text-xs ${
              isActive
                ? 'bg-primary-50 text-primary-600'
                : 'bg-neutral-100 text-neutral-500'
            }`}
          >
            {count}
          </span>
        )}
      </button>
    );
  };

  return (
    <div
      className="pt-8 min-h-screen w-full"
      data-testid="print-shop-queue"
      data-queue={origin}
    >
      <ToastContainer />
      <div className="mb-8 flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <Title>{printShopStrings.queue.titles[origin]}</Title>
          {printShopStrings.queue.subtitles[origin] && (
            <p
              className="text-sm text-neutral-500"
              data-testid="print-shop-queue-subtitle"
            >
              {printShopStrings.queue.subtitles[origin]}
            </p>
          )}
        </div>
        <Button
          variant="OUTLINED"
          isProcessing={isFetching && !isLoading}
          onClick={onRefresh}
        >
          {printShopStrings.queue.refreshButton}
        </Button>
      </div>

      <div className="shadow-md bg-white rounded-lg">
        <div
          className="flex flex-wrap items-center gap-1 border-b border-neutral-100 px-4"
          data-testid="ticket-status-tabs"
        >
          {tabs.map(renderTab)}
          {renderTab('all')}
        </div>

        <PastOrderLookup />

        <div className="px-4 py-3 border-b border-neutral-100 flex flex-wrap items-center gap-3">
          <TextInput
            data-testid="ticket-search"
            icon={HiOutlineSearch}
            placeholder={printShopStrings.queue.searchPlaceholder}
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            className="max-w-md"
          />
          <Select
            data-testid="ticket-asset-kind-filter"
            value={assetKindFilter}
            onChange={(event) => {
              setAssetKindFilter(event.target.value as AssetKind | '');
              setPage(1);
            }}
            className="w-[200px]"
          >
            <option value="">{printShopStrings.queue.assetKindFilterAll}</option>
            <option value="book">{printShopStrings.queue.assetTypeBook}</option>
            <option value="tb_export">{printShopStrings.queue.assetTypeExport}</option>
          </Select>
        </div>

        {isError && (
          <div className="px-4 pt-4">
            <Notification
              type="FAILURE"
              message={printShopStrings.queue.loadError}
            />
          </div>
        )}

        {isLoading ? (
          <div className="px-4 py-10 flex justify-center">
            <Spinner size="lg" aria-label={printShopStrings.queue.titles[origin]} />
          </div>
        ) : (
          <div className="px-4 py-2 overflow-x-auto">
            <Table data-testid="print-shop-queue-table">
              <Table.Head className="whitespace-nowrap text-gray-900 capitalize border-b border-t">
                <Table.HeadCell className="px-4 py-3">
                  {printShopStrings.queue.columns.order}
                </Table.HeadCell>
                <Table.HeadCell className="px-4 py-3">
                  {printShopStrings.queue.columns.member}
                </Table.HeadCell>
                <Table.HeadCell className="px-4 py-3">
                  {printShopStrings.queue.columns.asset}
                </Table.HeadCell>
                <Table.HeadCell className="px-4 py-3">
                  {printShopStrings.queue.columns.quantity}
                </Table.HeadCell>
                <Table.HeadCell className="px-4 py-3">
                  {printShopStrings.queue.columns.paidAt}
                </Table.HeadCell>
                <Table.HeadCell className="px-4 py-3">
                  {printShopStrings.queue.columns.ageDays}
                </Table.HeadCell>
                <Table.HeadCell className="px-4 py-3">
                  {printShopStrings.queue.columns.status}
                </Table.HeadCell>
                <Table.HeadCell className="px-4 py-3">
                  {printShopStrings.queue.columns.trackingRef}
                </Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y whitespace-nowrap">
                {tickets.map((ticket) => {
                  const slaBreach = isSlaBreach(ticket);
                  return (
                    <Table.Row
                      key={ticket.orderId}
                      data-testid={`ticket-row-${ticket.orderId}`}
                      role="link"
                      tabIndex={0}
                      aria-label={`${ticket.member.name} — ${ticket.assetTitle}`}
                      className={`border-b cursor-pointer hover:bg-[#EDFAFC] focus:outline-none focus:ring-2 focus:ring-primary-300 ${
                        slaBreach ? 'border-l-4 border-l-red-500' : ''
                      }`}
                      onClick={(event) => onRowClick(event, ticket.orderId)}
                      onAuxClick={(event) => {
                        if (event.button === 1) {
                          onRowClick(event, ticket.orderId);
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          navigate(ticketPath(ticket.orderId));
                        }
                      }}
                    >
                      <Table.Cell className="px-4 py-3 font-mono text-xs">
                        <div className="flex items-center gap-2">
                          <Link
                            to={ticketPath(ticket.orderId)}
                            data-testid={`order-link-${ticket.orderId}`}
                            onClick={(event) => event.stopPropagation()}
                            onAuxClick={(event) => event.stopPropagation()}
                            className="text-neutral-800 hover:underline"
                          >
                            {ticket.afyOrderNumber ??
                              shortOrderId(ticket.orderId)}
                          </Link>
                          <button
                            type="button"
                            data-testid={`copy-order-id-${ticket.orderId}`}
                            title={
                              copiedId === ticket.orderId
                                ? printShopStrings.queue.copied
                                : ticket.afyOrderNumber
                                  ? printShopStrings.queue.copyOrderNumber
                                  : printShopStrings.queue.copyOrderId
                            }
                            onClick={(event) =>
                              onCopyOrderId(
                                event,
                                ticket.orderId,
                                ticket.afyOrderNumber ?? ticket.orderId,
                              )
                            }
                            className="text-neutral-400 hover:text-neutral-700"
                          >
                            <HiOutlineClipboardCopy className="h-4 w-4" />
                          </button>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="text-neutral-800 text-sm font-semibold">
                            {ticket.member.name}
                          </span>
                          <span className="text-neutral-500 text-xs">
                            {ticket.member.email}
                          </span>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <span className="text-neutral-800">
                            {ticket.assetTitle}
                          </span>
                          {(ticket.assetKind ||
                            ticket.hasCover ||
                            ticket.itp?.included) && (
                            <div className="flex items-center gap-1.5">
                              {ticket.assetKind && (
                                <Badge
                                  color="gray"
                                  className="w-fit"
                                  data-testid={`asset-kind-${ticket.orderId}`}
                                >
                                  {ticket.assetKind === 'book'
                                    ? printShopStrings.queue.assetTypeBook
                                    : printShopStrings.queue.assetTypeExport}
                                </Badge>
                              )}
                              {ticket.hasCover && (
                                <Badge
                                  color="info"
                                  className="w-fit"
                                  title={printShopStrings.queue.coverHint}
                                  data-testid={`asset-cover-${ticket.orderId}`}
                                >
                                  {printShopStrings.queue.coverBadge}
                                </Badge>
                              )}
                              {/* The order includes an Instant Trust Package
                                (itp-mvp): it ships in the same box. */}
                              {ticket.itp?.included && (
                                <Badge
                                  color="teal"
                                  className="w-fit"
                                  title={
                                    ticket.itp.ready
                                      ? printShopStrings.queue.itpReadyHint
                                      : printShopStrings.queue.itpHint
                                  }
                                  data-testid={`asset-itp-${ticket.orderId}`}
                                  data-ready={ticket.itp.ready}
                                >
                                  {ticket.itp.ready
                                    ? printShopStrings.queue.itpReadyBadge
                                    : printShopStrings.queue.itpBadge}
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                      </Table.Cell>
                      <Table.Cell className="px-4 py-3">
                        {ticket.quantity}
                      </Table.Cell>
                      <Table.Cell className="px-4 py-3">
                        {formatDate(ticket.paidAt)}
                      </Table.Cell>
                      <Table.Cell className="px-4 py-3">
                        {slaBreach ? (
                          <span
                            className="inline-flex items-center gap-1 font-bold text-red-600"
                            data-testid={`sla-breach-${ticket.orderId}`}
                            title={formatMessage(
                              ticket.status === 'itp_pending'
                                ? printShopStrings.queue.slaTooltipItp
                                : printShopStrings.queue.slaTooltip,
                              { days: String(SLA_THRESHOLD_DAYS) },
                            )}
                          >
                            {ticket.ageDays}d
                            <HiOutlineExclamation className="h-4 w-4" />
                          </span>
                        ) : (
                          `${ticket.ageDays}d`
                        )}
                      </Table.Cell>
                      <Table.Cell className="px-4 py-3">
                        <Badge
                          color={statusBadgeColor(
                            // The queue endpoint never returns an imported row —
                            // that status is absent from ABO_VISIBLE_STATUSES on
                            // the server. The cast records that the narrowing is
                            // the SERVER's guarantee, not an assumption here.
                            ticket.status as TicketStatus,
                          )}
                          className="w-fit"
                        >
                          {statusText(ticket.status)}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell className="px-4 py-3 text-xs">
                        {ticket.trackingRef ?? printShopStrings.queue.noTracking}
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
            {!isError && tickets.length === 0 && (
              <p className="text-neutral-500 text-sm px-4 py-6">
                {printShopStrings.queue.empty}
              </p>
            )}
          </div>
        )}

        <div className="border-t border-neutral-100" data-testid="queue-pagination">
          <PaginationFlowBite
            tablePagination={{
              pageSize,
              total: meta?.total ?? 0,
              lastPage: Math.max(1, Math.ceil((meta?.total ?? 0) / pageSize)),
              currentPage: page,
              onChange: (next: number) => setPage(next),
            }}
            perPageOptions={PRINT_SHOP_PER_PAGE_OPTIONS}
            handlePerPage={({ value }) => {
              setPageSize(value);
              setPage(1);
            }}
          />
        </div>
      </div>

      <p className="px-1 py-3 text-xs text-neutral-400">
        {formatMessage(printShopStrings.queue.slaNote, {
          days: String(SLA_THRESHOLD_DAYS),
        })}
      </p>
    </div>
  );
}
